import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MissionHeader from './components/MissionHeader';
import CodeEditor from './components/CodeEditor';
import ExecutionOutput from './components/ExecutionOutput';
import MobileWorkspace from './components/MobileWorkspace';
import Icon from '../../components/AppIcon';


const MissionWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Mock user data
  const mockUser = {
    id: 1,
    username: "alex_developer",
    email: "alex@example.com",
    level: 3,
    progress: 65
  };

  // Mock mission data
  const mockMission = {
    id: 1,
    title: "Web Element Interaction Basics",
    description: "Learn to find and interact with web elements using Selenium WebDriver",
    difficulty: "Beginner",
    estimatedTime: "15 min",
    points: 100,
    completedBy: 1247,
    progress: 0,
    objective: "Create a Selenium script that navigates to a webpage, finds specific elements, and performs basic interactions like clicking buttons and filling forms.",
    requirements: [
      "Import necessary Selenium modules",
      "Initialize a WebDriver instance",
      "Navigate to the target webpage",
      "Find elements using various locator strategies",
      "Perform click and text input operations",
      "Capture a screenshot of the final state"
    ],
    hints: [
      "Use driver.get() to navigate to a webpage",
      "Try different locator strategies: ID, class name, XPath",
      "Remember to add explicit waits for dynamic content",
      "Use driver.save_screenshot() to capture the final state"
    ],
    documentationLink: "https://selenium-python.readthedocs.io/",
    exampleLink: "https://github.com/SeleniumHQ/selenium/tree/trunk/py/test/selenium/webdriver"
  };

  // State management
  const [code, setCode] = useState(`from selenium import webdriver
from selenium.webdriver.common.by import By
from selenium.webdriver.support.ui import WebDriverWait
from selenium.webdriver.support import expected_conditions as EC
import time

# Initialize the WebDriver
driver = webdriver.Chrome()

try:
    # Navigate to the target webpage
    driver.get("https://example.com")
    
    # Wait for page to load
    wait = WebDriverWait(driver, 10)
    
    # Your code here - find and interact with elements
    
    # Take a screenshot
    driver.save_screenshot("final_state.png")
    
    print("Mission completed successfully!")
    
finally:
    # Clean up
    driver.quit()`);

  const [language, setLanguage] = useState('python');
  const [autoSave, setAutoSave] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false); // Start with instructions collapsed
  const [isMobile, setIsMobile] = useState(false);

  // Mock execution data
  const [logs, setLogs] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [executionMetrics, setExecutionMetrics] = useState({
    executionTime: 0,
    memoryUsage: 0,
    networkRequests: 0,
    containerStatus: 'Idle'
  });

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-save functionality
  useEffect(() => {
    if (autoSave) {
      const timer = setTimeout(() => {
        // Mock auto-save
        console.log('Code auto-saved');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [code, autoSave]);

  const handleRunCode = async () => {
    setIsRunning(true);
    setLogs([]);
    setScreenshots([]);
    setValidationResults([]);
    
    // Mock execution process
    const mockLogs = [
      { timestamp: '14:32:01', type: 'info', message: 'Starting Selenium WebDriver...' },
      { timestamp: '14:32:02', type: 'info', message: 'Chrome browser initialized successfully' },
      { timestamp: '14:32:03', type: 'info', message: 'Navigating to https://example.com' },
      { timestamp: '14:32:04', type: 'success', message: 'Page loaded successfully' },
      { timestamp: '14:32:05', type: 'info', message: 'Searching for elements...' },
      { timestamp: '14:32:06', type: 'success', message: 'Element found: <button id="submit-btn">' },
      { timestamp: '14:32:07', type: 'info', message: 'Performing click action...' },
      { timestamp: '14:32:08', type: 'success', message: 'Click action completed' },
      { timestamp: '14:32:09', type: 'info', message: 'Taking screenshot...' },
      { timestamp: '14:32:10', type: 'success', message: 'Screenshot saved: final_state.png' },
      { timestamp: '14:32:11', type: 'success', message: 'Mission completed successfully!' }
    ];

    const mockScreenshots = [
      {
        url: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&h=600&fit=crop',
        step: 'Initial Page Load',
        description: 'Homepage loaded with all elements visible',
        timestamp: '14:32:03'
      },
      {
        url: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=600&fit=crop',
        step: 'Form Interaction',
        description: 'Form fields filled with test data',
        timestamp: '14:32:07'
      },
      {
        url: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&h=600&fit=crop',
        step: 'Final State',
        description: 'Page after successful form submission',
        timestamp: '14:32:10'
      }
    ];

    const mockValidation = [
      {
        testName: 'Element Location Test',
        status: 'passed',
        description: 'Successfully located target elements using provided selectors',
        executionTime: 245,
        details: 'Found 3 elements:\n- Submit button (ID: submit-btn)\n- Email input (Name: email)\n- Password input (Name: password)'
      },
      {
        testName: 'Interaction Test',
        status: 'passed',
        description: 'Successfully performed click and input operations',
        executionTime: 156,
        details: 'Completed interactions:\n- Filled email field\n- Filled password field\n- Clicked submit button'
      },
      {
        testName: 'Screenshot Capture',
        status: 'passed',
        description: 'Screenshot captured and saved successfully',
        executionTime: 89,
        details: 'Screenshot saved as: final_state.png\nFile size: 245KB\nResolution: 1920x1080'
      }
    ];

    // Simulate real-time log streaming
    for (let i = 0; i < mockLogs?.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 500));
      setLogs(prev => [...prev, mockLogs?.[i]]);
    }

    // Add screenshots progressively
    setTimeout(() => setScreenshots([mockScreenshots?.[0]]), 1500);
    setTimeout(() => setScreenshots(prev => [...prev, mockScreenshots?.[1]]), 3500);
    setTimeout(() => setScreenshots(prev => [...prev, mockScreenshots?.[2]]), 5000);

    // Add validation results
    setTimeout(() => setValidationResults(mockValidation), 5500);

    // Update metrics
    setTimeout(() => {
      setExecutionMetrics({
        executionTime: 5420,
        memoryUsage: 128,
        networkRequests: 12,
        containerStatus: 'Completed'
      });
      setIsRunning(false);
    }, 6000);
  };

  const handleResetCode = () => {
    setCode(`from selenium import webdriver
from selenium.webdriver.common.by import By
from selenium.webdriver.support.ui import WebDriverWait
from selenium.webdriver.support import expected_conditions as EC
import time

# Initialize the WebDriver
driver = webdriver.Chrome()

try:
    # Navigate to the target webpage
    driver.get("https://example.com")
    
    # Wait for page to load
    wait = WebDriverWait(driver, 10)
    
    # Your code here - find and interact with elements
    
    # Take a screenshot
    driver.save_screenshot("final_state.png")
    
    print("Mission completed successfully!")
    
finally:
    # Clean up
    driver.quit()`);
    setLogs([]);
    setScreenshots([]);
    setValidationResults([]);
    setExecutionMetrics({
      executionTime: 0,
      memoryUsage: 0,
      networkRequests: 0,
      containerStatus: 'Idle'
    });
  };

  const handleLogout = () => {
    navigate('/user-login');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={mockUser} 
        onLogout={handleLogout}
      />
      
      <div className="content-offset h-screen flex flex-col">
        {/* Mission Header */}
        <MissionHeader
          mission={mockMission}
          onReset={handleResetCode}
          onToggleInstructions={() => setShowInstructions(!showInstructions)}
          showInstructions={showInstructions}
        />

        {/* Workspace Content - Improved layout when instructions are showing */}
        <div className={`flex-1 overflow-hidden transition-all duration-300 ${showInstructions ? 'workspace-with-instructions' : ''}`}>
          {isMobile ? (
            <MobileWorkspace
              code={code}
              onChange={setCode}
              language={language}
              onLanguageChange={setLanguage}
              onRun={handleRunCode}
              isRunning={isRunning}
              autoSave={autoSave}
              onToggleAutoSave={() => setAutoSave(!autoSave)}
              logs={logs}
              screenshots={screenshots}
              validationResults={validationResults}
              executionMetrics={executionMetrics}
            />
          ) : (
            <div className="flex h-full">
              {/* Left Panel - Code Editor - Available even when instructions are open */}
              <div className="w-1/2 border-r border-subtle">
                <CodeEditor
                  code={code}
                  onChange={setCode}
                  language={language}
                  onLanguageChange={setLanguage}
                  onRun={handleRunCode}
                  isRunning={isRunning}
                  autoSave={autoSave}
                  onToggleAutoSave={() => setAutoSave(!autoSave)}
                />
              </div>

              {/* Right Panel - Execution Output - Available even when instructions are open */}
              <div className="w-1/2">
                <ExecutionOutput
                  logs={logs}
                  screenshots={screenshots}
                  validationResults={validationResults}
                  executionMetrics={executionMetrics}
                  isRunning={isRunning}
                />
              </div>
            </div>
          )}
        </div>

        {/* Bottom Status Bar */}
        <div className="bg-card border-t border-subtle px-4 py-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Icon name="Zap" size={12} />
                <span>Selenium Sandbox</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Globe" size={12} />
                <span>Container: selenium-python-3.9</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} />
                <span>Session: {Math.floor(Date.now() / 1000) % 3600}s</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Save" size={12} />
                <span>{autoSave ? 'Auto-save: ON' : 'Auto-save: OFF'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionWorkspace;